import { ApolloError, useMutation } from '@apollo/client'
import {
  KebabMenuOption,
  SnackbarVariant,
  useSnackbar
} from '@focaldata/cin-ui-components'
import { createContext, useContext } from 'react'
import { SET_ENTRIES_HIDDEN } from '../../../data/gql/questionnaire/mutations/setEntriesHidden'
import { draftQuestionnaireRefetchQuery } from '../../../data/gql/questionnaire/queries'
import { LoggerErrorType } from '../../../data/logger'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { captureApolloError } from '../../../utils/HelperFunctions'
import {
  WaveVisibility,
  canToggleWaveVisibility,
  isHiddenInWave,
  waveVisibilityActionLabel
} from './waveVisibility'
import { waveVisibilityErrorMessage } from './waveVisibilityErrors'

const SectionHiddenInWaveContext = createContext(false)

/** Set by a section around its entries: whether the section is hidden in this wave. */
export const SectionHiddenInWaveProvider = SectionHiddenInWaveContext.Provider

/**
 * An entry's visibility in the current wave, from data its card already has:
 * its own setting, and its section's through context. Only tracker routes carry
 * a waveId (TrackerRoutes), so on any other project nothing is hidden.
 */
export const useWaveVisibility = (
  entryItem: DraftQuestionnaireEntry['entryItem']
): WaveVisibility & { enabled: boolean } => {
  const enabled = !!useOptionalWaveId()
  const sectionHidden = useContext(SectionHiddenInWaveContext)

  return {
    enabled,
    hidden: enabled && isHiddenInWave(entryItem),
    hiddenBySection: enabled && sectionHidden
  }
}

/** The Hide/Unhide menu item for one entry, or undefined outside a wave. */
export const useWaveVisibilityAction = (
  entryId: string,
  entryItem: DraftQuestionnaireEntry['entryItem']
):
  | Pick<KebabMenuOption, 'textItem' | 'disabled' | 'onClickItem'>
  | undefined => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { enabled, ...visibility } = useWaveVisibility(entryItem)
  const { enqueueSnackbar } = useSnackbar()
  // No onError: with one, Apollo resolves a failed mutation instead of rejecting it.
  const [setEntriesHidden, { loading }] = useMutation(SET_ENTRIES_HIDDEN, {
    context: { clientName: 'questionnaire' },
    refetchQueries: [
      draftQuestionnaireRefetchQuery(projectId, surveyId, waveId)
    ],
    awaitRefetchQueries: true
  })

  if (!enabled) {
    return undefined
  }

  const canToggle = !loading && canToggleWaveVisibility(visibility)

  const toggle = async () => {
    if (!canToggle) {
      return
    }
    try {
      await setEntriesHidden({
        variables: {
          input: {
            projectId,
            surveyId,
            // Without a waveId the write would go to the legacy questionnaire.
            waveId,
            entryIds: [entryId],
            hidden: !visibility.hidden
          }
        }
      })
    } catch (error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'setEntriesHiddenV2',
        error as ApolloError
      )
      enqueueSnackbar(waveVisibilityErrorMessage(error), {
        variant: SnackbarVariant.Error
      })
    }
  }

  return {
    textItem: waveVisibilityActionLabel(visibility),
    disabled: !canToggle,
    onClickItem: toggle
  }
}
